import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { api } from '../utils/api';

export default function PaymentResult() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();

  const success = searchParams.get('success') === 'true';
  const entityType = searchParams.get('type') || 'order';
  const entityId = searchParams.get('id');

  const [verified, setVerified] = useState(null); // null = checking, true/false = result

  useEffect(() => {
    if (success && entityType && entityId) {
      // Clear cart on successful payment
      clearCart();

      // Verify with backend that webhook was received
      const checkStatus = async () => {
        try {
          const data = await api.getPaymentStatus(entityType, parseInt(entityId, 10));
          setVerified(data.status === 'paid');
        } catch {
          // If polling fails, trust the redirect param
          setVerified(true);
        }
      };
      checkStatus();
    } else {
      setVerified(false);
    }
  }, [success, entityType, entityId, clearCart]);

  const containerStyle = {
    minHeight: '100vh',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  };

  const cardStyle = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    padding: '3rem 2rem',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
  };

  const iconStyle = {
    fontSize: '3.5rem',
    marginBottom: '1rem',
    display: 'block',
  };

  const titleStyle = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
  };

  const subtitleStyle = {
    color: 'var(--text-muted)',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    marginBottom: '2rem',
  };

  const primaryBtnStyle = {
    display: 'inline-block',
    padding: '0.6rem 2rem',
    background: 'var(--orange)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-heading)',
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
    marginRight: '0.75rem',
  };

  const secondaryBtnStyle = {
    display: 'inline-block',
    padding: '0.6rem 2rem',
    background: 'transparent',
    color: 'var(--orange)',
    border: '1px solid var(--orange)',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-heading)',
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
  };

  if (success) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <span style={{ ...iconStyle, color: '#22c55e' }}>&#10003;</span>
          <h1 style={titleStyle}>{t('payment.successTitle')}</h1>
          <p style={subtitleStyle}>{t('payment.successMessage')}</p>

          {verified === null && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
              {t('payment.verifying')}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Link to="/profile" style={primaryBtnStyle}>
              {t('payment.viewOrders')}
            </Link>
            <Link to="/" style={secondaryBtnStyle}>
              {t('payment.continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <span style={{ ...iconStyle, color: '#ef4444' }}>&#10007;</span>
        <h1 style={titleStyle}>{t('payment.failedTitle')}</h1>
        <p style={subtitleStyle}>{t('payment.failedMessage')}</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Link to="/cart" style={primaryBtnStyle}>
            {t('payment.tryAgain')}
          </Link>
          <Link to="/" style={secondaryBtnStyle}>
            {t('payment.goHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
